import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/news";
import { formatDate, formatViews } from "@/utils/news";

type NewsCardProps = {
  article: NewsArticle;
  variant?: "grid" | "horizontal" | "compact";
};

export default function NewsCard({ article, variant = "grid" }: NewsCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex items-start gap-3 py-3 cursor-pointer"
      >
        <span className="mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 text-xs font-bold">
          {article.views > 300000 ? "热" : "新"}
        </span>
        <div className="min-w-0">
          <p className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </p>
          <p className="mt-1 text-xs text-foreground-400 flex items-center gap-2">
            <span>{article.category}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-background-100/60 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-56 h-40 sm:h-36 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 吃瓜爆料网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 rounded-md bg-primary-50 text-primary-600 text-xs font-medium whitespace-nowrap">
              {article.category}
            </span>
            {article.tags[0] && (
              <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                {article.tags[0]}
              </span>
            )}
          </div>
          <h3 className="text-base md:text-lg font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
          <div className="mt-auto pt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {formatDate(article.publishTime)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col rounded-lg bg-background-50 border border-background-200 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-full aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 吃瓜爆料网`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 flex flex-col p-4">
        <span className="self-start px-2 py-0.5 rounded-md bg-primary-50 text-primary-600 text-xs font-medium mb-2">
          {article.category}
        </span>
        <h3 className="text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
        <div className="mt-auto pt-3 flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {formatDate(article.publishTime).slice(5, 10)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}